import { useTranslation } from 'react-i18next'

import Icon from '../../../../ui/Icon'
import { ListRow } from '../../../../listItem/listRow/ListRow'
import { ListCard } from '../../../../listItem/listCard/ListCard'
import { ListingDetails } from '../../../../listing/ListingDetails'

import { CONFIG } from '../../../../../config'
import { DisplayType } from '../../../../../types/listing'
import { LoginAuditEntry, LoginStatus } from '../../../../../types/audit/audit'
import {
  displayDate,
  displayLocalDateTimeHourMinute,
} from '../../../../../utils/dateUtils'

const STATUS_BADGE_CLASS: Record<LoginStatus, string> = {
  [LoginStatus.SUCCESS]: 'badge badge-success',
  [LoginStatus.FAILED]: 'badge badge-danger',
}

const EMPTY = <span className="muted">&mdash;</span>

export const LoginAuditList = () => {
  const { t, i18n } = useTranslation('audit')

  const renderItem = (item: LoginAuditEntry, viewMode: DisplayType) => {
    const date = `${displayDate(item.createdDate, i18n.language)} ${displayLocalDateTimeHourMinute(item.createdDate)}`
    const status = (
      <span className={STATUS_BADGE_CLASS[item.status]}>
        {t(`audit.logins.status.${item.status}`)}
      </span>
    )
    const account = item.userId
      ? t('audit.logins.account.known')
      : t('audit.logins.account.unknown')

    if (viewMode === DisplayType.LIST) {
      return (
        <ListRow
          key={item.id}
          cells={[
            { key: 'date', className: 'mono', content: date },
            { key: 'email', content: item.email },
            { key: 'account', content: account },
            { key: 'ip', className: 'mono', content: item.ipAddress || EMPTY },
            { key: 'location', content: item.location || EMPTY },
            { key: 'status', content: status },
          ]}
        />
      )
    }

    return (
      <ListCard
        key={item.id}
        stripe={item.status === LoginStatus.SUCCESS ? 'success' : 'danger'}
        title={item.email}
        subtitle={
          <>
            <Icon name="clock" size={12} />
            <span>{date}</span>
          </>
        }
        status={status}
        fields={[
          { key: 'ip', label: t('audit.logins.columns.ip'), value: item.ipAddress || EMPTY },
          { key: 'location', label: t('audit.logins.columns.location'), value: item.location || EMPTY, mono: false },
          { key: 'account', label: t('audit.logins.columns.account'), value: account, mono: false },
        ]}
      />
    )
  }

  return (
    <ListingDetails<LoginAuditEntry>
      endpoint={`${CONFIG.BOUNCE_API}/api/v1/audit/logins`}
      initialViewMode={DisplayType.LIST}
      displaySearch
      searchPlaceholder={t('audit.search-placeholder')}
      tableColumns={[
        { key: 'date', label: t('audit.logins.columns.date') },
        { key: 'email', label: t('audit.logins.columns.email') },
        { key: 'account', label: t('audit.logins.columns.account') },
        { key: 'ip', label: t('audit.logins.columns.ip') },
        { key: 'location', label: t('audit.logins.columns.location') },
        { key: 'status', label: t('audit.logins.columns.status') },
      ]}
      renderItem={renderItem}
    />
  )
}
